import { useMutationState } from '@tanstack/react-query'
import type { MutationStatus } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context'
import { Snackbar } from 'components/Snackbar'
import { queryKeys } from 'constants/queryKeys'
import { friendAnalyticsStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import { toErrorMessage } from 'hooks/utils'
import type { TFriendAnalyticsCollectResult } from 'types/friendAnalytics'
import { buildCollectMessage } from './friendAnalyticsView'

type CollectionState = {
  id: number
  status: MutationStatus
  data: TFriendAnalyticsCollectResult | undefined
  error: Error | null
}

/** 画面遷移でアンマウントされない App から、取得の進行と結果を知らせる。 */
export function FriendAnalyticsCollectSnackbar() {
  const { user } = useAuth()
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null
  return user ? (
    <UserCollectionSnackbar
      key={`${user.uid}:${organizationId}`}
      userId={user.uid}
      organizationId={organizationId}
    />
  ) : null
}

function UserCollectionSnackbar({
  userId,
  organizationId,
}: {
  userId: string
  organizationId: number | null
}) {
  const collections = useMutationState<CollectionState>({
    filters: {
      mutationKey: [queryKeys.FRIEND_ANALYTICS_COLLECT, userId, organizationId],
      exact: true,
    },
    select: (mutation) => ({
      id: mutation.mutationId,
      status: mutation.state.status,
      // このキーへ登録する取得処理の応答型。
      data: mutation.state.data as TFriendAnalyticsCollectResult | undefined,
      error: mutation.state.error,
    }),
  })
  // 組織の切り替えで作り直されたとき、表示済みの結果を出し直さない
  const [settledBeforeMount] = useState(
    () =>
      new Set(
        collections
          .filter((collection) => collection.status !== 'pending')
          .map((collection) => collection.id),
      ),
  )
  const latest = collections[collections.length - 1]
  return latest && !settledBeforeMount.has(latest.id) ? (
    <CollectionSnackbar key={`${latest.id}:${latest.status}`} state={latest} />
  ) : null
}

function CollectionSnackbar({ state }: { state: CollectionState }) {
  const [open, setOpen] = useState(true)
  const close = useCallback(() => setOpen(false), [])
  const pending = state.status === 'pending'
  const failed = state.status === 'error'
  const message = pending
    ? friendAnalyticsStrings.collect.running
    : failed
      ? toErrorMessage(state.error, friendAnalyticsStrings.errors.collectFailed)
      : buildCollectMessage(state.data, friendAnalyticsStrings.collect)

  if (!message) return null

  return (
    <Snackbar
      open={open}
      message={
        <>
          <p className="font-semibold">{friendAnalyticsStrings.title}</p>
          <p>{message}</p>
        </>
      }
      variant={
        failed
          ? 'error'
          : !pending &&
              state.data?.hasRemaining === false &&
              !state.data.skippedByLock
            ? 'success'
            : 'info'
      }
      position="bottom-right"
      autoHideDuration={pending ? undefined : 8_000}
      onClose={close}
    />
  )
}
